import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { GripVertical, X } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface BaseWidgetProps {
  title: string
  icon?: React.ReactNode
  isLoading?: boolean
  error?: string | null
  children: React.ReactNode
  className?: string
  isEditing?: boolean
  onRemove?: () => void
  dragHandleProps?: Record<string, unknown>
}

export function BaseWidget({
  title,
  icon,
  isLoading,
  error,
  children,
  className,
  isEditing,
  onRemove,
  dragHandleProps,
}: BaseWidgetProps) {
  return (
    <Card className={cn('h-full flex flex-col', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          {isEditing && (
            <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          {icon}
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </div>
        {isEditing && onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : error ? (
          <div className="text-sm text-destructive">{error}</div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )
}

// Stat widget for simple number displays
interface StatWidgetProps {
  title: string
  value: string | number
  subtitle?: string
  icon?: React.ReactNode
  iconBgColor?: string // Background color for icon (e.g., '#7BBDE8', '#FF2F6D')
  trend?: React.ReactNode | {
    value: number
    isPositive: boolean
  }
  badge?: React.ReactNode // Optional badge to show (e.g., "3 Overdue", "+12.4%")
  isLoading?: boolean
  error?: string
  className?: string
  valueClassName?: string
}

export function StatWidget({
  title,
  value,
  subtitle,
  icon,
  iconBgColor,
  trend,
  badge,
  isLoading,
  error,
  className,
  valueClassName,
}: StatWidgetProps) {
  if (isLoading) {
    return (
      <Card className={cn('h-full', className)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-10 w-10 rounded-xl" />
          <Skeleton className="h-4 w-16" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-8 w-28" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={cn('h-full', className)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">{error}</div>
        </CardContent>
      </Card>
    )
  }

  // Check if trend is a React node or an object
  const renderTrend = () => {
    if (!trend) return null
    if (typeof trend === 'object' && 'value' in trend && 'isPositive' in trend) {
      return (
        <span className={cn(
          'text-xs font-medium',
          trend.isPositive ? 'text-green-600' : 'text-red-600'
        )}>
          {trend.isPositive ? '+' : ''}{trend.value}%
        </span>
      )
    }
    return trend
  }

  return (
    <Card className={cn('h-full min-h-[140px]', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 pt-4">
        {/* Icon with colored background */}
        {icon && iconBgColor ? (
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${iconBgColor}20` }}
          >
            <div style={{ color: iconBgColor }}>
              {icon}
            </div>
          </div>
        ) : icon ? (
          <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-muted">
            {icon}
          </div>
        ) : null}
        {/* Badge or Trend in top right */}
        <div className="flex items-center gap-2">
          {renderTrend()}
          {badge}
        </div>
      </CardHeader>
      <CardContent className="pb-5">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{title}</p>
        <div className={cn('text-2xl font-bold', valueClassName)}>{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  )
}

